/* TestimonialSilhouette — themed placeholder avatar for testimonial
 * cards on /how-we-help-writers when the admin hasn't supplied a real
 * portrait URL. Deterministic (name-hashed) preset pick so the same
 * author always renders the same silhouette across page views.
 *
 * Palette is intentionally sepia to match the Fluent-parchment card
 * background — never gaudy, always period-appropriate.
 */
import React from 'react';

// Six presets — three seated / three writing poses — hand-drawn as
// inline SVG so no network round-trip is needed.
const PRESETS = [
  // 0 · quill portrait
  <>
    <circle cx="16" cy="10" r="5" />
    <path d="M6 26c1-6 6-9 10-9s9 3 10 9v2H6z" />
    <path d="M20 10l4-4 1 1-4 4z" opacity="0.65" />
  </>,
  // 1 · book portrait
  <>
    <circle cx="16" cy="10" r="5" />
    <path d="M4 20h24v6l-12 2-12-2z" opacity="0.85" />
    <path d="M16 22v6" strokeWidth="0.6" opacity="0.6" />
  </>,
  // 2 · profile with glasses
  <>
    <path d="M8 12c0-5 3-9 8-9s8 4 8 9v3c0 3-3 6-8 6s-8-3-8-6z" />
    <circle cx="13" cy="12" r="1.4" fill="var(--paper,#fdf6e4)" />
    <circle cx="19" cy="12" r="1.4" fill="var(--paper,#fdf6e4)" />
    <path d="M6 28c2-4 6-6 10-6s8 2 10 6" opacity="0.85" />
  </>,
  // 3 · sun-hat silhouette
  <>
    <circle cx="16" cy="12" r="4.5" />
    <path d="M4 14c3-4 7-6 12-6s9 2 12 6" opacity="0.6" />
    <path d="M6 28c1-5 6-8 10-8s9 3 10 8z" />
  </>,
  // 4 · scribe with scroll
  <>
    <circle cx="16" cy="9" r="4.5" />
    <path d="M7 26c0-5 4-9 9-9s9 4 9 9v3H7z" />
    <path d="M22 18c1 1 1 3 0 4l-3-1" opacity="0.7" />
  </>,
  // 5 · classical bust
  <>
    <path d="M12 4h8l2 6h-12z" opacity="0.7" />
    <circle cx="16" cy="13" r="5" />
    <path d="M6 28c1-6 5-10 10-10s9 4 10 10z" />
  </>,
];

// Sepia palette per preset — subtle variation keeps a shelf of cards
// from looking cloned.
const TONES = ['#a1602e', '#8b4a26', '#a67142', '#7a4022', '#96552e', '#8f4d24'];

function hashString(s) {
  let h = 0;
  for (let i = 0; i < (s || '').length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

export default function TestimonialSilhouette({ name, size = 32, presetIdx, testId }) {
  // Explicit preset override wins; otherwise deterministic hash on
  // the author name so the same person always shows the same face.
  const raw = (typeof presetIdx === 'number' && presetIdx >= 0)
    ? presetIdx
    : hashString(name || '');
  const idx = ((raw % PRESETS.length) + PRESETS.length) % PRESETS.length;
  const tone = TONES[idx];
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 32 32"
      width={size}
      height={size}
      role="img"
      aria-hidden
      data-testid={testId}
      style={{
        flex:         '0 0 auto',
        borderRadius: '9999px',
        border:       '1px solid var(--gold, #c6a664)',
        background:   '#fdf6e4',
        fill:         tone,
        stroke:       tone,
        strokeWidth:  0.4,
      }}
    >
      {PRESETS[idx]}
    </svg>
  );
}
